/**
 * User Status Checker Component
 *
 * Check if a user is banned or muted and view their current status
 */

import { useState } from 'react';
import { getUserStatus, unbanUser, unmuteUser, type UserModerationStatus } from '../../../services/moderation';

export function UserStatusChecker() {
  const [username, setUsername] = useState('');
  const [topicId, setTopicId] = useState('');
  const [status, setStatus] = useState<UserModerationStatus | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const handleCheck = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccessMessage(null);
    setStatus(null);

    if (!username) {
      setError('Username is required');
      return;
    }

    setIsLoading(true);

    try {
      const result = await getUserStatus(username, topicId || undefined);
      setStatus(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to check user status');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUnban = async () => {
    if (!username || !status?.userId) return;

    const confirmed = confirm(`Are you sure you want to unban user ${username}?`);
    if (!confirmed) return;

    setError(null);
    setSuccessMessage(null);

    try {
      await unbanUser(status.userId, topicId || undefined, 'Unbanned via moderator dashboard');
      alert('User unbanned successfully');
      // Refresh status
      const result = await getUserStatus(username, topicId || undefined);
      setStatus(result);
    } catch (err) {
      alert('Failed to unban user: ' + (err instanceof Error ? err.message : 'Unknown error'));
    }
  };

  const handleUnmute = async () => {
    if (!username || !status?.userId) return;

    const confirmed = confirm(`Are you sure you want to unmute user ${username}?`);
    if (!confirmed) return;

    setError(null);
    setSuccessMessage(null);

    try {
      await unmuteUser(status.userId, topicId || undefined, 'Unmuted via moderator dashboard');
      alert('User unmuted successfully');
      // Refresh status
      const result = await getUserStatus(username, topicId || undefined);
      setStatus(result);
    } catch (err) {
      alert('Failed to unmute user: ' + (err instanceof Error ? err.message : 'Unknown error'));
    }
  };

  return (
    <div>
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Check User Status</h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
          View a user's ban or mute status and take actions
        </p>
      </div>

      <form onSubmit={handleCheck} className="space-y-4">
        <div>
          <label htmlFor="username" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            User ID *
          </label>
          <input
            type="text"
            id="username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            placeholder="Enter user ID (e.g., user-123)"
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            required
          />
        </div>

        <div>
          <label htmlFor="topicId" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Topic ID (optional)
          </label>
          <input
            type="text"
            id="topicId"
            value={topicId}
            onChange={(e) => setTopicId(e.target.value)}
            placeholder="Leave empty to check global status"
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        <button
          type="submit"
          disabled={isLoading}
          className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-4 rounded-lg transition-colors disabled:opacity-50"
        >
          {isLoading ? 'Checking...' : 'Check Status'}
        </button>
      </form>

      {error && (
        <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800 rounded-lg">
          {error}
        </div>
      )}

      {successMessage && (
        <div className="mt-4 p-3 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 border border-green-200 dark:border-green-800 rounded-lg">
          {successMessage}
        </div>
      )}

      {status && (
        <div className="mt-6 space-y-4">
          {/* Ban Status */}
          <div className={`p-4 rounded-lg border ${
            status.isBanned
              ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
              : 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
          }`}>
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-semibold text-gray-900 dark:text-gray-100">Ban Status</h4>
              {status.isBanned ? (
                <span className="px-2 py-1 bg-red-600 text-white text-xs font-medium rounded">BANNED</span>
              ) : (
                <span className="px-2 py-1 bg-green-600 text-white text-xs font-medium rounded">NOT BANNED</span>
              )}
            </div>

            {status.isBanned && (
              <>
                <div className="text-sm text-gray-700 dark:text-gray-300 space-y-1">
                  <p><strong>Reason:</strong> {status.banReason}</p>
                  {status.banExpiresAt && (
                    <p><strong>Expires:</strong> {new Date(status.banExpiresAt).toLocaleString()}</p>
                  )}
                  {!status.banExpiresAt && (
                    <p className="text-red-700 dark:text-red-300"><strong>Type:</strong> Permanent Ban</p>
                  )}
                </div>
                <button
                  onClick={handleUnban}
                  className="mt-3 w-full bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                >
                  Unban User
                </button>
              </>
            )}
          </div>

          {/* Mute Status */}
          <div className={`p-4 rounded-lg border ${
            status.isMuted
              ? 'bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-800'
              : 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
          }`}>
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-semibold text-gray-900 dark:text-gray-100">Mute Status</h4>
              {status.isMuted ? (
                <span className="px-2 py-1 bg-orange-600 text-white text-xs font-medium rounded">MUTED</span>
              ) : (
                <span className="px-2 py-1 bg-green-600 text-white text-xs font-medium rounded">NOT MUTED</span>
              )}
            </div>

            {status.isMuted && (
              <>
                <div className="text-sm text-gray-700 dark:text-gray-300 space-y-1">
                  <p><strong>Reason:</strong> {status.muteReason}</p>
                  {status.muteExpiresAt && (
                    <p><strong>Expires:</strong> {new Date(status.muteExpiresAt).toLocaleString()}</p>
                  )}
                  {!status.muteExpiresAt && (
                    <p className="text-orange-700 dark:text-orange-300"><strong>Type:</strong> Permanent Mute</p>
                  )}
                </div>
                <button
                  onClick={handleUnmute}
                  className="mt-3 w-full bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                >
                  Unmute User
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
